import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import {
  DEFAULT_FEEDBACK_COMPLETE_FILTER,
  FEEDBACK_FILTER_KEYS,
} from '../constants/feedback.constants';
import {
  toFeedbackFilterSearch,
  toFeedbackFilterValues,
  toFeedbackListParams,
} from '../utils/feedbackFilters';

const route = getRouteApi('/_auth/feedback/list/');

const DEFAULT_FILTER_VALUES = {
  [FEEDBACK_FILTER_KEYS.completeStatus]: DEFAULT_FEEDBACK_COMPLETE_FILTER,
};

const FILTER_KEYS_WITH_DEFAULT: readonly string[] = [FEEDBACK_FILTER_KEYS.completeStatus];

const normalizeFilterValues = (values: Record<string, string>) =>
  toFeedbackFilterValues(toFeedbackFilterSearch(values));

export function useFeedbackListFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toFeedbackFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues: DEFAULT_FILTER_VALUES,
    rangeKeys: FILTER_KEYS_WITH_DEFAULT,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toFeedbackFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const searchBox = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  const params = useMemo(() => toFeedbackListParams(search), [search]);

  return { ...filters, ...searchBox, params, search };
}
